import axios from 'axios'
import storage from 'store'
import { useRootStore } from '@/stores/root'
import authentication from './authentication'
import cache from './cache'

// Port di services/http.js (Vue 2/Vuex) → Vue 3/Pinia.
// - store Vuex → useRootStore() (Pinia), chiamato a runtime (pinia già attiva)
//   per evitare problemi di ordine d'inizializzazione / cicli.
// - process.env.VUE_APP_API_URL → import.meta.env.VITE_API_URL.

axios.interceptors.request.use(
  (config) => {
    const token = storage.get('authToken') || null
    const updatedConfig = { ...config }
    if (token) {
      updatedConfig.headers = {
        ...updatedConfig.headers,
        Authorization: `Bearer ${token}`,
      }
    }
    return updatedConfig
  },
  (error) => Promise.reject(error),
)

axios.interceptors.response.use(
  (response) => {
    const token = response.data.token || null
    if (token) {
      storage.set('authToken', token)
    }
    return response
  },
  (error) => {
    // error.response è undefined su errori di rete/CORS → senza `?.` l'interceptor
    // stesso lanciava TypeError. Solo un vero 401 deve forzare il logout.
    if (error.response?.status === 401) {
      const rootStore = useRootStore()
      authentication.logout()
      rootStore.setMenuOpened(false)
      rootStore.setUser(null)
      // Lazy import per evitare il ciclo http → router → componenti → store → http
      // (il ciclo statico lasciava useXStore undefined a seconda dell'ordine d'import).
      import('@/router').then(({ default: router }) => router.push('/login'))
    }
    return Promise.reject(error)
  },
)

const getResponse = async (
  { method, path, externalUrl, data, params, headers, responseType },
  forceCall,
) => {
  const rootStore = useRootStore()
  const agent = rootStore.agent || null
  let paramsUpdate = params
  if (agent) {
    if (params) {
      paramsUpdate = {
        ...params,
        usrmst: agent.usrage,
      }
    } else {
      paramsUpdate = {
        usrmst: agent.usrage,
      }
    }
  }
  const cachedResponse =
    method === 'get' &&
    !externalUrl &&
    cache.getCachedResponse(path, paramsUpdate, storage.get('authToken'))
  if (!cachedResponse || forceCall) {
    const axiosOptions = {
      method,
      url: externalUrl || `${import.meta.env.VITE_API_URL}${path}`,
      data,
      params:
        method === 'get' && !externalUrl
          ? { prevCache: new Date().getTime(), ...paramsUpdate }
          : paramsUpdate,
      headers,
      responseType,
    }
    const response = await axios(axiosOptions)
    if (method === 'get' && !externalUrl) {
      cache.cacheResponse(path, paramsUpdate, storage.get('authToken'), response)
    }
    return response
  }
  return cachedResponse
}

export default {
  async request(options, forceCall) {
    const response = await getResponse(options, forceCall)
    if (options.responseType) return response
    return response.data
  },
}
